import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ForecastSeriesCompletedResponse } from '@api-response-types/forecast-series.response';
import { GetMeasuresDto } from '@api-types/dtos/get-measures.dto';
import { UpdateForecastSeriesDto } from '@api-types/dtos/update-forecast-series.dto';
import { FeatureFlagId } from '@api-types/enums/feature-flag-id.enum';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { IntervalType } from '@api-types/enums/interval-type.enum';
import { Resolution } from '@api-types/enums/resolution.enum';
import { convertFiltersMapToRecord } from '@api-types/helper/helper';
import { FilterValue } from '@api-types/types/filter-value.type';
import { Filter } from '@api-types/types/filter.type';
import { selectFeatureFlagById } from '@core/store/feature-flags/feature-flags.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { ChangeChartTimeRangeService } from 'demand-forecasting/services/chart/change-chart-time-range.service';
import { ErrorMeasureStateService } from 'demand-forecasting/store/error-measure/error-measure-state.service';
import { ForecastsHttpClient } from 'demand-forecasting/services/http/forecast-http-client';
import { ForecastVersionHttpClient } from 'demand-forecasting/services/http/forecast-version-http-client.service';
import {
  selectForecastVersionById,
  selectForecastVersionByQueryParam,
} from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import { ConfigurationRouterHelperService } from 'demand-forecasting/store/router-state/configuration-router-helper.service';
import {
  selectResolution,
  selectVersionId,
} from 'demand-forecasting/store/router-state/router.selectors';
import { ToastrService } from 'ngx-toastr';
import {
  concatMap,
  EMPTY,
  finalize,
  map,
  pipe,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { catchError, filter, withLatestFrom } from 'rxjs/operators';
import { ConfigurationsSelectors } from '../configurations/configurations.selectors';
import { FilterActions } from '../filter/filter.actions';
import { selectAppliedFilters } from '../filter/filter.selectors';
import { ForecastVersionActions } from '../forecast-version/forecast-version.actions';
import { ForecastSeriesActions } from './forecast-series.actions';
import {
  selectConfidenceLevel,
  selectFutureErrorMeasure,
  selectIntervalType,
} from './forecast-series.selectors';

@Injectable()
export class ForecastSeriesEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly configurationRouterHelperService = inject(
    ConfigurationRouterHelperService,
  );

  private readonly toastService = inject(ToastrService);
  private readonly dialogRef = inject(MatDialog);

  private readonly translateService = inject(TranslateService);
  private readonly changeChartTimeRangeService = inject(
    ChangeChartTimeRangeService,
  );

  private readonly errorMeasureService = inject(ErrorMeasureStateService);

  private readonly forecastsHttpClient = inject(ForecastsHttpClient);
  private readonly forecastVersionHttpClient = inject(
    ForecastVersionHttpClient,
  );

  // TODO test, this is basically the heart of the app
  // TODO write cleaner without subscribe
  getForecastSeries$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ForecastSeriesActions.getForecastSeriesByVersionId),
        tap(() => {
          this.store.dispatch(
            ForecastSeriesActions.setIsForecastSeriesFetching({
              isFetching: true,
            }),
          );
          this.store.dispatch(
            ForecastSeriesActions.setForecastSeriesHasFailed({
              hasFailed: false,
            }),
          );
        }),
        switchMap(({ versionId }) =>
          this.store.select(selectForecastVersionByQueryParam).pipe(
            filter((version) => version !== undefined),
            take(1),
            map((version) => ({ versionId, version })),
          ),
        ),
        filter(({ version }) => {
          if (version.status !== ForecastVersionStatus.FINISHED) {
            this.store.dispatch(
              ForecastSeriesActions.setIsForecastSeriesFetching({
                isFetching: false,
              }),
            );
            return false;
          }
          return true;
        }),
        concatMap(({ versionId }) =>
          this.forecastsHttpClient.getAvailableFilters(versionId).pipe(
            tap((availableFilters) => {
              this.store.dispatch(
                FilterActions.setAvailableFilters({ availableFilters }),
              );
              this.store.dispatch(
                FilterActions.getAllAvailableFilters({
                  forecastVersionId: versionId,
                }),
              );
            }),
            withLatestFrom(
              this.store.select(selectResolution),
              this.store.select(
                ConfigurationsSelectors.selectAdditionalTimeSeriesNamesByUrlParam,
              ),
              this.store.select(
                ConfigurationsSelectors.selectFactorColumnByUrlParam,
              ),
              this.store.select(selectIntervalType),
              this.store.select(selectAppliedFilters),
            ),
            switchMap(
              ([
                //  we need to make sure the available filters are fetched before the forecast series but we don't use them in the effect
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                _,
                resolution,
                additionalTimeSeriesNames,
                factorColumn,
                intervalType,
                appliedFilters,
              ]) => {
                if (!resolution) {
                  // TODO add error handling
                  console.error(
                    'no resolution found for fetching forecast series.',
                  );
                  return EMPTY;
                }

                this.store.dispatch(
                  FilterActions.resetFilterCombinationForecastSeries(),
                );

                return this.forecastsHttpClient
                  .getForecastSeries(
                    versionId,
                    resolution as Resolution,
                    additionalTimeSeriesNames,
                    factorColumn,
                    intervalType,
                    appliedFilters
                      ? convertFiltersMapToRecord(appliedFilters)
                      : {},
                  )
                  .pipe(
                    tap((res) => {
                      this.resetErrorMeasures();

                      this.store.dispatch(
                        ForecastVersionActions.getAllForecastVersionsForCofiguration(
                          {
                            configurationId: res.configurationId,
                          },
                        ),
                      );

                      // make sure that the target unit is set correctly as param
                      const selectedForecastVersion = this.store.selectSignal(
                        selectForecastVersionById(versionId),
                      );
                      if (
                        selectedForecastVersion()?.status ===
                        ForecastVersionStatus.FINISHED
                      ) {
                        const finishedRes =
                          res as ForecastSeriesCompletedResponse;
                        this.configurationRouterHelperService.updateTargetUnit(
                          finishedRes.yAxis,
                        );

                        if (finishedRes.invalidAppliedFilterValues) {
                          this.handleResetOfInvalidAppliedFilters(
                            finishedRes.invalidAppliedFilterValues,
                          );
                        }
                      }
                    }),
                    tap((res) => {
                      this.store.dispatch(
                        ForecastSeriesActions.setForecastSeries({
                          forecastSeries: res,
                        }),
                      );

                      this.store.dispatch(
                        ForecastSeriesActions.recalculateSelectedFutureErrorMeasureValue(),
                      );
                    }),
                    catchError((error: HttpErrorResponse) => {
                      Sentry.captureException(error, {
                        tags: {
                          feature: 'forecast-series',
                          operation: 'get-forecast-series',
                        },
                        extra: {
                          versionId,
                          resolution,
                          statusCode: error.status,
                          errorMessage: error.message,
                        },
                      });
                      this.store.dispatch(
                        ForecastSeriesActions.setForecastSeriesHasFailed({
                          hasFailed: true,
                        }),
                      );
                      return EMPTY;
                    }),
                  );
              },
            ),
            catchError((error: HttpErrorResponse) => {
              Sentry.captureException(error, {
                tags: {
                  feature: 'forecast-series',
                  operation: 'get-forecast-series-filters',
                },
                extra: {
                  versionId,
                  statusCode: error.status,
                  errorMessage: error.message,
                },
              });
              this.store.dispatch(
                ForecastSeriesActions.setForecastSeriesHasFailed({
                  hasFailed: true,
                }),
              );
              return EMPTY;
            }),
            finalize(() => {
              this.store.dispatch(
                ForecastSeriesActions.setIsForecastSeriesFetching({
                  isFetching: false,
                }),
              );
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  getForecastSeriesOnProgressFinished = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ForecastVersionActions.setForecastVersion),
        withLatestFrom(this.store.select(selectVersionId)),
        map(([{ forecastVersion }, versionId]) => {
          if (
            forecastVersion.status === ForecastVersionStatus.FINISHED &&
            versionId === forecastVersion.id
          ) {
            this.store.dispatch(
              ForecastSeriesActions.getForecastSeriesByVersionId({ versionId }),
            );
          }
          return versionId;
        }),
      ),
    { dispatch: false },
  );

  applyForecastSeriesOptions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ForecastSeriesActions.applyForecastSeriesOptions),
        withLatestFrom(
          this.store.select(selectForecastVersionByQueryParam),
          this.store.select(
            ConfigurationsSelectors.selectConfigurationByUrlParam,
          ),
          this.store.select(selectResolution),
          this.store.select(
            ConfigurationsSelectors.selectFactorColumnByUrlParam,
          ),
          this.store.select(
            ConfigurationsSelectors.selectAdditionalTimeSeriesNamesByUrlParam,
          ),
          this.store.select(selectIntervalType),
          this.store.select(selectConfidenceLevel),
          this.store.select(selectAppliedFilters),
        ),
        pipe(tap(() => this.resetErrorMeasures())),
        map(
          ([
            { options, filterCombinationName },
            version,
            configuration,
            resolution,
            factorColumn,
            additionalTimeSeriesNames,
            intervalType,
            confidenceLevel,
            appliedFilters,
          ]) => {
            if (!version) {
              return;
            }
            let updateFilters: Map<string, FilterValue[]> = new Map();
            // in case the action's payload contains filters, use them -> otherwise use the applied filters from global state
            if (options.filters) {
              options.filters?.forEach((value, key) => {
                updateFilters.set(key, value);
              });
            } else {
              updateFilters = appliedFilters;
            }

            // construct the dto, if action's payload contains a value, use it -> otherwise use the value from global state
            const updateForecastSeriesDto: UpdateForecastSeriesDto = {
              additionalTimeSeriesNames:
                options.additionalTimeSeriesNames ?? additionalTimeSeriesNames,
              resolution: options.resolution ?? (resolution as Resolution),
              factorColumn,
              filters: convertFiltersMapToRecord(updateFilters),
              intervalType:
                options.intervalType === undefined
                  ? (intervalType as IntervalType) || null
                  : (options.intervalType ?? undefined),
              confidenceLevel: options.confidenceLevel ?? confidenceLevel,
              loadPredictionExplanation: false,
            };
            this.store.dispatch(
              FilterActions.resetFilterCombinationForecastSeries(),
            );
            this.store.dispatch(
              FilterActions.refetchFilterCombinationForecastSeriesOnConfigurationChange(),
            );
            this.store.dispatch(
              ForecastSeriesActions.setIsForecastSeriesUpdatePending({
                isPending: true,
              }),
            );
            this.forecastsHttpClient
              .updateForecastSeries(updateForecastSeriesDto, version.forecastId)
              .pipe(
                map((updatedForecastSeries) => {
                  // case: user used filters which caused an empty response
                  if (updatedForecastSeries.predictions.data.length === 0) {
                    this.handleEmptyForecastSeriesResponse(appliedFilters);
                    return;
                  }

                  // case: user wanted to apply and save the filter combination
                  // -> since we only want to save the filter combination if the result is not empty, we dispatch is here
                  if (
                    filterCombinationName &&
                    filterCombinationName.length > 0
                  ) {
                    this.store.dispatch(
                      FilterActions.createAndSaveFilterCombination({
                        name: filterCombinationName,
                      }),
                    );
                  }

                  this.store.dispatch(
                    ForecastSeriesActions.setUpdatedForecastSeries({
                      updatedForecastSeries,
                      yAxisFallback:
                        configuration?.startForecastConfig?.target || '',
                    }),
                  );
                  this.store.dispatch(
                    FilterActions.setAppliedFilters({
                      appliedFilters: updateFilters,
                    }),
                  );
                  this.store.dispatch(
                    ForecastSeriesActions.recalculateSelectedFutureErrorMeasureValue(),
                  );
                  // only close dialog in success case, otherwise it should stay open
                  this.dialogRef.closeAll();
                }),
                catchError((error: HttpErrorResponse) => {
                  Sentry.captureException(error, {
                    tags: {
                      feature: 'forecast-series',
                      operation: 'apply-forecast-series-options',
                    },
                    extra: {
                      versionId: version?.id,
                      forecastId: version?.forecastId,
                      configurationId: configuration?.id,
                      statusCode: error.status,
                      errorMessage: error.message,
                    },
                  });
                  this.toastService.error(
                    this.translateService.instant(
                      'UTILITY.FORECAST_SERIES_WONT_LOAD',
                    ),
                  );
                  return EMPTY;
                }),
                finalize(() => {
                  this.store.dispatch(
                    ForecastSeriesActions.setIsForecastSeriesUpdatePending({
                      isPending: false,
                    }),
                  );
                }),
              )
              .subscribe();
          },
        ),
      ),
    {
      dispatch: false,
    },
  );
  resetErrorMeasures() {
    this.store.dispatch(
      ForecastSeriesActions.setSelectedFutureErrorMeasureValue({
        futureErrorMeasureValue: undefined,
      }),
    );
    this.store.dispatch(
      ForecastSeriesActions.setSelectedFutureErrorMeasure({
        measureForFutureErrorMeasure:
          this.errorMeasureService.selectedErrorMeasureOptions().measureType,
      }),
    );
  }

  recalculateFutureErrorMeasureValue = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ForecastSeriesActions.recalculateSelectedFutureErrorMeasureValue,
        ),
        switchMap(() =>
          this.errorMeasureService.isPreferenceSettled$.pipe(take(1)),
        ),
        withLatestFrom(
          this.store.select(
            selectFeatureFlagById(FeatureFlagId.future_error_measure),
          ),
          this.store.select(selectVersionId),
          this.store.select(selectFutureErrorMeasure),
          this.store.select(selectResolution),
          this.store.select(selectAppliedFilters),
        ),
        // when the feature flag is disabled, we don't want to calculate the future error measure
        filter(
          ([, futureErrorMeasureFeatureFlag]) =>
            futureErrorMeasureFeatureFlag?.enabled,
        ),
        map(([, , versionId, selectedMeasure, resolution, appliedFilters]) => {
          if (versionId && resolution) {
            const body: GetMeasuresDto = {
              filters: appliedFilters
                ? convertFiltersMapToRecord(appliedFilters)
                : {},
              resolution: resolution as Resolution,
              measureConfigs: [{ measure: selectedMeasure }],
              measureGroupColumns:
                this.errorMeasureService.selectedErrorMeasureOptions()
                  .measureGroupColumns,
            };
            this.store.dispatch(
              ForecastSeriesActions.setIsFutureErrorMeasureCalculating({
                isCalculating: true,
              }),
            );
            this.forecastVersionHttpClient
              .getFutureErrorMeasures(versionId, body)
              .pipe(
                map((res) =>
                  this.store.dispatch(
                    ForecastSeriesActions.setSelectedFutureErrorMeasureValue({
                      futureErrorMeasureValue:
                        res?.aggregatedMeasures[0] ?? undefined,
                    }),
                  ),
                ),
                finalize(() =>
                  this.store.dispatch(
                    ForecastSeriesActions.setIsFutureErrorMeasureCalculating({
                      isCalculating: false,
                    }),
                  ),
                ),
              )
              .subscribe();
          }
        }),
      ),
    { dispatch: false },
  );

  private handleResetOfInvalidAppliedFilters(
    invalidAppliedFilterValues: Filter[],
  ) {
    const invalidAppliedFilters = invalidAppliedFilterValues;
    if (
      invalidAppliedFilters !== undefined &&
      invalidAppliedFilters?.length > 0
    ) {
      this.toastService.warning(
        this.translateService.instant(
          'FILTER.FILTER_NOT_APPLIED_TOAST_MESSAGE',
          {
            invalidFilterValues: invalidAppliedFilters
              .map((item) => `${item.column}: ${item.values.join(', ')}`)
              .map((value) => value.split(';')),
          },
        ),
        this.translateService.instant('FILTER.FILTER_NOT_APPLIED_TOAST_TITLE'),
        {
          closeButton: true,
          tapToDismiss: false,
          timeOut: 0,
        },
      );
      this.store.dispatch(FilterActions.resetAppliedFilters());
      this.store.dispatch(FilterActions.resetSelectedFilters());
    }
  }

  private handleEmptyForecastSeriesResponse(
    appliedFilters: Map<string, FilterValue[]>,
  ) {
    this.toastService.info(
      this.translateService.instant(
        'FILTER.SELECTED_FILTERS_CAUSE_EMPTY_RESULT',
      ),
      this.translateService.instant(
        'FILTER.PLEASE_SELECT_DIFFERENT_FILTER_COMBINATIONS',
      ),
    );
    // reset the filters to the previous state
    // if filters were applied, use them -> otherwise use the available filters
    if (appliedFilters.size > 0) {
      this.store.dispatch(
        FilterActions.setSelectedFilters({
          selectedFilters: appliedFilters,
        }),
      );
    }
  }
}
